import { Button, Form, InputNumber, Modal } from 'antd';
import { Gauge } from 'lucide-react';
import { useState } from 'react';

interface TruckOdometerCardProps {
  currentOdometerKm: number;
  odometerRecordedAt: string;
  canManage: boolean;
  onRecord: (odometerKm: number) => Promise<boolean>;
}

/** The odometer only moves through a reading (CLAUDE.md: no direct edit) — this card shows the current value and is the one place that records a new one. */
export function TruckOdometerCard({ currentOdometerKm, odometerRecordedAt, canManage, onRecord }: TruckOdometerCardProps) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm<{ odometerKm: number }>();

  async function submit(values: { odometerKm: number }) {
    setSubmitting(true);
    try {
      const recorded = await onRecord(values.odometerKm);
      if (recorded) {
        form.resetFields();
        setOpen(false);
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="flex items-center justify-between gap-4 rounded-lg border border-line bg-surface px-4 py-3">
      <div className="flex items-center gap-3">
        <div className="grid size-9 place-items-center rounded-lg bg-primary/10 text-primary">
          <Gauge className="size-4" />
        </div>
        <div>
          <p className="font-medium text-fg">{currentOdometerKm.toLocaleString()} km</p>
          <p className="text-sm text-muted">as of {new Date(odometerRecordedAt).toLocaleString()}</p>
        </div>
      </div>
      {canManage && (
        <Button size="small" onClick={() => setOpen(true)}>
          Record reading
        </Button>
      )}
      <Modal
        open={open}
        title="Record odometer reading"
        onOk={() => form.submit()}
        onCancel={() => setOpen(false)}
        confirmLoading={submitting}
        destroyOnClose
        okText="Record"
      >
        <Form<{ odometerKm: number }> form={form} layout="vertical" requiredMark={false} onFinish={submit} initialValues={{ odometerKm: currentOdometerKm }}>
          <Form.Item
            name="odometerKm"
            label="New odometer reading (km)"
            rules={[
              { required: true, message: 'Enter the reading' },
              { type: 'number', min: currentOdometerKm, message: `Must be at least ${currentOdometerKm.toLocaleString()} km` },
            ]}
          >
            <InputNumber className="w-full" min={currentOdometerKm} step={10} autoFocus />
          </Form.Item>
        </Form>
      </Modal>
    </section>
  );
}
